import {
  DeleteOutline,
  RoundaboutRight,
  ThumbDownAltOutlined,
  TokenOutlined,
  VerifiedOutlined,
} from "@mui/icons-material";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ConstructionIcon from "@mui/icons-material/Construction";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridActionsCellItem, GridColDef, GridRowParams, GridRowsProp, useGridApiRef } from "@mui/x-data-grid-premium";
import moment from "moment";
import { ChangeEvent, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useOutletContext } from "react-router";
import { debounce } from "throttle-debounce";
import AssetClassChip from "../../../../../Components/Misc/Chips/AssetChip/asset-class-chip";
import AssetTypeFromObjectChip from "../../../../../Components/Misc/Chips/AssetChip/asset-type-chip-from-object";
import { ModalContext } from "../../../../../Components/Modals";
import ModalConfirmAction, {
  ModalConfirmActionElementProps,
} from "../../../../../Components/Modals/ModalConfirmAction/modal-confirm-action";
import ModalEditMetadata, {
  ModalEditMetadataElementProps,
} from "../../../../../Components/Modals/ModalEditMetadata/modal-edit-metadata";
import ModalNewAssignment, {
  ModalNewAssignmentElementProps,
} from "../../../../../Components/Modals/ModalNewAssignment/modal-new-assignment";
import ModalOrderFileFixingAction, {
  ModalOrderFileFixingActionElementProps,
} from "../../../../../Components/Modals/ModalOrderFileFixing/modal-order-file-fixing-action";
import ModalRequestPurge, {
  ModalRequestPurgeElementProps,
} from "../../../../../Components/Modals/ModalPurgeAsset/modal-request-purge";
import ModalSetFollowers, {
  ModalSetFollowersElementProps,
} from "../../../../../Components/Modals/ModalSetFollowers/modal-set-followers";
import AssetPanel from "../../../../../Components/Panels/AssetPanel/asset-panel";
import NewRequestPanel from "../../../../../Components/Panels/NewRequestPanels/new-request-panel";
import DataTable, { CellSize } from "../../../../../Components/Table/table";
import TableCell2Lines from "../../../../../Components/Table/TableCell2Lines/table-cell-2-lines";
import TableCellDateTime from "../../../../../Components/Table/TableCellDateTime/table-cell-date-time";
import TableCellMovie from "../../../../../Components/Table/TableCellMovie/table-cell-movie";
import TableCellTypography from "../../../../../Components/Table/TableCellTypography/table-cell-typography";
import { assetService, cacheService, ingestService, workflowService } from "../../../../../Core/Api/services";
import { AclTypes } from "../../../../../Core/Api/Types/acl-types";
import { DbAssetObjectInfos } from "../../../../../Core/Api/Types/db-asset-object-infos";
import { Licences } from "../../../../../Core/Api/Types/licences";
import { ObjectClass } from "../../../../../Core/Api/Types/object-class";
import { ObjectStatus } from "../../../../../Core/Api/Types/object-status";
import { ObjectType } from "../../../../../Core/Api/Types/object-type";
import { PlatformMode } from "../../../../../Core/Api/Types/platform-mode";
import GlobalConst from "../../../../../Core/Utils/Constants/globals";
import { routePaths } from "../../../../../Router/router";
import { UserHasAcl, UserHasAcls, UserHasLicence } from "../../../../../Utils/helpers";
import useCustomSnackbar from "../../../../../Utils/Hooks/useCustomSnackbar";
import TableUuids from "../../../../../Components/Table/table-uuids";

export interface IngestRejectionsRows {
  id: number;
  assetId: number;
  assetType: ObjectType;
  assetClass: ObjectClass;
  assetName: string;
  assetStatus: ObjectStatus;
  createdAt: string | null;
  createdByUserName: string | null;
  message: string[] | null;
  rejectedBy: string | null;
  assetParentTitleId: number | null;
  assetParentTitleName: string | null;
  assetParentObjectName: string | undefined;
  assetProviderId: number;
  assetProviderName: string;
  followersIds: number[];
  rejectionDate: string | null;
  bypassAllowed: boolean;
  asset: DbAssetObjectInfos | null;
}

export interface IngestRejectionsProps {
  rows: IngestRejectionsRows[];
  filtersData: {
    type: ObjectType[];
    class: ObjectClass[];
    rejectedBy: string[];
  };
}

function IngestRejected() {
  const { t } = useTranslation();
  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const isDrive = cacheService.GetPlatformMode() === PlatformMode.Browser;

  const [filtersData, setFiltersData] = useState<IngestRejectionsProps["filtersData"]>({
    type: [],
    class: [],
    rejectedBy: [],
  });

  // Outlet context used to pass props to parent DashboardLayout through react router dom
  const { selectedItemIds, setSelectedItems } = useOutletContext<{
    setSelectedItems: React.Dispatch<React.SetStateAction<number[]>>;
    selectedItemIds: number[];
  }>();

  // Loading
  const [isLoading, setIsLoading] = useState(true);

  // Table filter
  const [filterType, setFilterType] = useState<ObjectType | "">("");
  const [filterClass, setFilterClass] = useState<ObjectClass | "">("");
  const [filterStatus, setFilterStatus] = useState<ObjectStatus | "">("");
  const [filterText, setFilterText] = useState("");
  const [withBypassFilter, setWithBypassFilter] = useState(false);

  // Table rows
  const [rows, setRows] = useState<GridRowsProp>([]);
  // Asset panel nad request panel
  const [openAssetPanel, setOpenAssetPanel] = useState(false);
  const [openNewRequestPanel, setOpenNewRequestPanel] = useState(false);

  const apiGrid = useGridApiRef();

  const [selectedAssetId, setSelectedAssetId] = useState<number>();
  const [selectedRowId, setSelectedRowId] = useState<number>();
  const enqueueCustomSnackbar = useCustomSnackbar();

  const handleFilterType = (event: SelectChangeEvent) => {
    const value = event.target.value as string;
    if (value === "") setFilterType("");
    else setFilterType(ObjectType[value]);
  };

  const handleFilterClass = (event: SelectChangeEvent) => {
    const value = event.target.value as string;
    if (value === "") setFilterClass("");
    else setFilterClass(ObjectClass[value]);
  };

  const handleFilterStatus = (event: SelectChangeEvent) => {
    const value = event.target.value as string;
    if (value === "") setFilterStatus("");
    else setFilterStatus(ObjectStatus[value]);
  };

  const handleClearFilters = () => {
    setFilterType("");
    setFilterClass("");
    setFilterStatus("");
    setFilterText("");
  };

  // search input management
  const updateSearchValue = useCallback(
    (newSearchValue: string) => {
      setFilterText(newSearchValue);
    },
    [filterText]
  );

  const debouncedUpdateSearchValue = useMemo(() => debounce(500, updateSearchValue), [updateSearchValue]);

  const handleSearchValueChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newSearchValue = event.target.value;
      debouncedUpdateSearchValue(newSearchValue);
    },
    [debouncedUpdateSearchValue]
  );

  // Row buttons
  const handleAskFix = (objectId: number) => {
    if (!openModal) {
      return;
    }

    openModal<ModalOrderFileFixingActionElementProps>(ModalOrderFileFixingAction, {
      objectId: objectId,
      onConfirm: () => {},
    });
  };

  const handleSetFollowers = (assetId: number, actualFollowersIds: number[]) => {
    if (!openModal) return;

    openModal<ModalSetFollowersElementProps>(ModalSetFollowers, {
      objectIds: [assetId],
      objectType: "asset",
      currentFollowersIds: actualFollowersIds,
    });
  };

  const handleBypass = (assetId: number) => {
    if (!openModal) return;

    openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
      displayComment: true,
      title: t("modal:title.bypassAssetRejection", { id: assetId }),
      onConfirm: (comment) => {
        workflowService.AssetByPassRejection(assetId, comment).then((response) => {
          if (response.rescode === GlobalConst.ELEVATION_RESPONSE_OBJECT_ID)
            enqueueCustomSnackbar(response.message!, "primary");
          else enqueueCustomSnackbar(t("alert:success.assetRejectionBypassed", { id: assetId }), "success");
        });
      },
    });
  };

  const handleNewAssignment = (assetId: number, assetProviderId: number) => {
    if (!openModal) return;
    openModal<ModalNewAssignmentElementProps>(ModalNewAssignment, {
      assetId,
      assetProviderId,
    });
  };

  const handleEditMetadataAndReprocessClick = (assetId: number) => {
    if (!openModal) return;

    assetService.GetObjectById(assetId).then((res) => {
      if (res.success && res.graph) {
        openModal<ModalEditMetadataElementProps>(ModalEditMetadata, {
          objects: [res.graph],
          onSuccess: () => {
            ingestService.ReprocessRequestAfterAutomatedRejection(assetId).then((response) => {
              enqueueCustomSnackbar(t("alert:success.assetIngestReprocess", { id: assetId }), "success");
            });
          },
        });
      } else enqueueCustomSnackbar(res.message || t("alert:error.assetNotFound"), "error");
    });
  };

  const handleDeleteAsset = (assetId: number, comment?: string) => {
    ingestService.DeleteRequest({ id: assetId, comment: comment || null }).then((response) => {
      if (response.objectId === GlobalConst.ELEVATION_RESPONSE_OBJECT_ID)
        enqueueCustomSnackbar(response.message!, "primary");
      else {
        if (response.success)
          enqueueCustomSnackbar(t("alert:success.deletedAssetSuccessfully", { id: assetId }), "success");
        else enqueueCustomSnackbar(t("alert:error.deletingAsset", { id: assetId, message: response.message }), "error");
      }
    });
  };

  const handleDeleteAssetClick = (assetId: number) => {
    if (!openModal) return;

    openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
      title: t("modal:title.confirmAssetDeletion"),
      displayComment: true,
      onConfirm: (comment?: string) => handleDeleteAsset(assetId, comment),
    });
  };

  const handlePurgeAssetClick = (assetId: number) => {
    if (!openModal) return;

    openModal<ModalRequestPurgeElementProps>(ModalRequestPurge, {
      title: t("modal:title.confirmAssetPurgeWorkflowRequest"),
      assetIds: [assetId],
    });
  };

  // Asset panel
  const hideAssetPanel = () => {
    setOpenAssetPanel(false);
  };

  const showAssetPanel = (e: GridRowParams) => {
    setSelectedAssetId(e.row["assetId"]);
    setSelectedRowId(e.row.id);
    if (!openAssetPanel) setOpenAssetPanel(true);
  };

  const hideNewRequestPanel = () => {
    setOpenNewRequestPanel(false);
  };

  // Acls
  const aclIngestRejectionsBypass = useMemo(
    () => UserHasAcl(AclTypes.aclIngestRejectionsBypass) || UserHasAcl(AclTypes.aclElevationWishlistCreate),
    []
  );
  const aclWorkflowAssignFollowers = useMemo(() => UserHasAcl(AclTypes.aclWorkflowAssignFollowers), []);
  const aclMetadataUpdate = useMemo(() => UserHasAcl(AclTypes.aclMetadataUpdate), []);
  const aclIngestDeleteRequest = useMemo(
    () => UserHasAcls([AclTypes.aclIngestDeleteRequest, AclTypes.aclElevationWishlistCreate]),
    []
  );
  const aclIngestAssignToLabProvider = useMemo(() => UserHasAcl(AclTypes.aclIngestAssignToLabProvider), []);
  const aclWorkflowPurgeCreate = useMemo(
    () => UserHasAcls([AclTypes.aclWorkflowPurgeCreate, AclTypes.aclElevationWishlistCreate]),
    []
  );

  // #region Grid definition
  let _rows: GridRowsProp = [];
  type Row = (typeof _rows)[number];

  const ingestProcessingColumns: GridColDef[] = useMemo(() => {
    const result: any[] = [
      {
        headerName: t("menu:item.objectId"),
        field: "assetId",
        minWidth: CellSize.small,
        flex: CellSize.small,
        renderCell: (data: Row) => (
          <Link
            color="primary"
            underline="hover"
            component={RouterLink}
            target="_blank"
            onClick={(e) => e.stopPropagation()}
            to={`${routePaths.asset.path}/${data.value}`}
          >
            {data.value}
          </Link>
        ),
      },
    ];
    if (!isDrive)
      result.push({
        headerName: t("common:title"),
        field: "assetParentTitleId",
        minWidth: CellSize.large,
        flex: CellSize.large,
        valueGetter: (params: any, row: any) => {
          if (!row) return;
          return `${row?.assetParentTitleName} ${row?.assetParentTitleId}`;
        },
        valueFormatter: (params: any, row: any) => {
          if (params) {
            return `${row.assetParentTitleName + " " || ""}#${row.assetParentTitleId || ""}`;
          } else return "";
        },
        renderCell: (data: Row) => (
          <TableCellMovie
            id={data?.row?.assetParentTitleId ?? data?.row?.assetId}
            name={data.row["assetParentTitleName"]}
            isDrive={data?.row?.assetParentTitleId === null}
          />
        ),
      });
    result.push(
      ...[
        {
          headerName: t("table:headerName.contentAndAsset"),
          field: "assetName",
          minWidth: CellSize.xlarge,
          flex: CellSize.xlarge,
          renderCell: (data: Row) => (
            <TableCell2Lines title={data?.row?.assetParentObjectName} desc={data?.row?.assetName} />
          ),
        },
        {
          headerName: t("common:type"),
          field: "assetType",
          minWidth: CellSize.small,
          flex: CellSize.small,
          type: "singleSelect",
          valueOptions: Object.keys(ObjectType),
          renderCell: (data: Row) => <AssetTypeFromObjectChip size="small" obj={data?.row?.asset} />,
        },
        {
          headerName: t("common:class"),
          field: "assetClass",
          type: "singleSelect",
          valueOptions: Object.keys(ObjectClass),
          minWidth: CellSize.xsmall,
          flex: CellSize.xsmall,
          renderCell: (data: Row) => <AssetClassChip size="small" classification={data.value} />,
        },
        {
          headerName: t("common:provider"),
          field: "assetProviderName",
          minWidth: CellSize.medium,
          flex: CellSize.medium,
          renderCell: (data: Row) => <TableCellTypography fontWeight={500} color="text.secondary" data={data.value} />,
        },
        {
          headerName: t("menu:title.rejectedAt"),
          field: "rejectionDate",
          type: "date",
          minWidth: CellSize.medium,
          flex: CellSize.medium,
          valueFormatter: (params: any, row: any) => {
            if (params) return moment(params.toString()).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS);
            else return "";
          },
          renderCell: (data: Row) => <TableCellDateTime data={data} />,
        },
        {
          headerName: t("table:headerName.rejection"),
          field: "message",
          minWidth: CellSize.xlarge,
          flex: CellSize.xlarge,
          renderCell: (data: Row) => (
            <Stack py={1} sx={{ maxHeight: 200, overflow: "auto" }}>
              {data.value.map((x, index) => (
                <Typography
                  key={index}
                  color={"text.secondary"}
                  sx={(t) => ({
                    ...(data.value.length > 1 &&
                      index !== 0 && {
                        mt: 0.5,
                        display: "list-item",
                      }),
                  })}
                >
                  {x}
                </Typography>
              ))}
              <Typography color="info.main" variant={"body2"} sx={{ wordBreak: "break-all", wordWrap: "break-word" }}>
                {data.row["assetStatus"] === ObjectStatus.RejectedAuto ? t("field:label.automated") : t("common:human")}
              </Typography>
            </Stack>
          ),
        },
        {
          headerName: t("common:createdBy"),
          field: "createdByUserName",
          minWidth: CellSize.medium,
          flex: CellSize.medium,
          renderCell: (data: Row) => <TableCellTypography fontWeight={500} color="text.secondary" data={data.value} />,
        },
        {
          headerName: t("common:createdAtLowerCase"),
          field: "createdAt",
          type: "date",
          minWidth: CellSize.medium,
          flex: CellSize.medium,
          valueFormatter: (params: any, row: any) => {
            if (params) return moment(params.toString()).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS);
            else return "";
          },
          renderCell: (data: Row) => <TableCellDateTime data={data} />,
        },
        {
          headerName: "",
          field: "actions",
          type: "actions",
          align: "right",
          minWidth: CellSize.actionsLarge,
          flex: CellSize.actionsLarge,
          getActions: (params: GridRowParams) => {
            if (!params) return;
            let menu: any[] = [];
            let alreadyFollowers = params.row["followersIds"] !== null && params.row["followersIds"].length > 0;

            if (!alreadyFollowers)
              menu.push(
                <GridActionsCellItem
                  icon={<GroupsOutlinedIcon />}
                  label={t("common:setFollowers")}
                  disabled={!aclWorkflowAssignFollowers}
                  onClick={() => handleSetFollowers(params.row["assetId"], params.row["followersIds"])}
                  showInMenu
                />
              );
            else
              menu.push(
                <Tooltip
                  title={`${params.row["followersIds"].length} follower${
                    params.row["followersIds"].length > 1 ? "s" : ""
                  }`}
                >
                  <GridActionsCellItem
                    icon={<GroupsOutlinedIcon />}
                    label={t("common:setFollowers")}
                    disabled={!aclWorkflowAssignFollowers}
                    onClick={() => handleSetFollowers(params.row["assetId"], params.row["followersIds"])}
                  />
                </Tooltip>
                //   <Chip
                //     onClick={() => handleSetFollowers(params.row["assetId"], params.row["followersIds"])}
                //     label={params.row["followersIds"].length}
                //     color={"info"}
                //     icon={<GroupsOutlinedIcon />}
                //     disabled={!aclWorkflowAssignFollowers}
                //   />
              );

            if (UserHasLicence(Licences.QuoteManagement))
              menu.push(
                <Tooltip title={t("common:requestFileFixing")} arrow>
                  <GridActionsCellItem
                    icon={<ConstructionIcon color="primary" />}
                    label={t("common:requestFileFixing")}
                    onClick={() => handleAskFix(params?.row?.assetId)}
                  />
                </Tooltip>
              );

            if (params?.row?.bypassAllowed && aclIngestRejectionsBypass)
              menu.push(
                <Tooltip title={t("tooltip:title.bypassThisRejection")} arrow>
                  <GridActionsCellItem
                    icon={<RoundaboutRight color="warning" />}
                    label={t("common:bypass")}
                    onClick={() => handleBypass(params.row["assetId"])}
                  />
                </Tooltip>
              );

            menu.push(
              <GridActionsCellItem
                icon={<PersonAddIcon />}
                label={t("common:newAssignment")}
                disabled={!aclIngestAssignToLabProvider}
                onClick={() => handleNewAssignment(params.row["assetId"], params.row["assetProviderId"])}
                showInMenu
              />
            );

            if (
              !params.row["message"]?.some((x) => x.includes("[Duration]")) &&
              !params.row["message"]?.some((x) => x.includes("[Timecode]")) &&
              !params.row["message"]?.some((x) => x.includes("[Audio Bit Depth]")) &&
              !params.row["message"]?.some((x) => x.includes("[System]")) &&
              !params.row["message"]?.some((x) => x.includes("Failed to decode frame"))
            ) {
              menu.push(
                <GridActionsCellItem
                  icon={
                    <Tooltip title={t("common:editMetadataAndReprocess")}>
                      <EditOutlinedIcon />
                    </Tooltip>
                  }
                  label={t("common:editMetadataAndReprocess")}
                  disabled={!aclMetadataUpdate || params?.row?.assetStatus === ObjectStatus.Rejected}
                  onClick={() => handleEditMetadataAndReprocessClick(params.row["assetId"])}
                  showInMenu={!aclMetadataUpdate || params?.row?.assetStatus === ObjectStatus.Rejected}
                />
              );
            }

            menu.push(
              <GridActionsCellItem
                icon={
                  <Tooltip title={t("modal:body.purgeMediaFiles")}>
                    <DeleteOutline color="error" />
                  </Tooltip>
                }
                label={t("modal:body.purgeMediaFiles")}
                color="error"
                disabled={!aclWorkflowPurgeCreate || params?.row?.assetStatus !== ObjectStatus.Rejected}
                onClick={() => handlePurgeAssetClick(params.row["assetId"])}
                showInMenu={!aclWorkflowPurgeCreate || params?.row?.assetStatus !== ObjectStatus.Rejected}
              />
            );

            menu.push(
              <GridActionsCellItem
                icon={<CancelOutlinedIcon color="error" />}
                label={t("common:delete")}
                color="error"
                disabled={!aclIngestDeleteRequest}
                onClick={() => handleDeleteAssetClick(params.row["assetId"])}
                showInMenu
              />
            );

            // id for the tutorial
            menu.push(<Box id="tutorial-ingest-rejected-menu"></Box>);

            return menu;
          },
        },
      ]
    );
    return result;
  }, []);
  // #endregion

  useEffect(() => {
    ingestService.GetRejectedIngestWorkflowsWithProviders().then(([request]) => {
      if (request) {
        setRows(request.rows);
        setFiltersData(request.filtersData);
      }
      setIsLoading(false);
    });
  }, []);

  const additionalToolbarFilters = (
    <>
      <Select
        displayEmpty
        IconComponent={ExpandMoreRoundedIcon}
        startAdornment={<TokenOutlined />}
        inputProps={{ "aria-label": "Without label" }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
        value={filterType}
        onChange={handleFilterType}
      >
        <MenuItem value="" selected>
          {t("common:type")}
        </MenuItem>
        {filtersData.type.length > 0 &&
          filtersData.type.map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
      </Select>
      <Select
        displayEmpty
        IconComponent={ExpandMoreRoundedIcon}
        startAdornment={<VerifiedOutlined />}
        inputProps={{ "aria-label": "Without label" }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
        value={filterClass}
        onChange={handleFilterClass}
      >
        <MenuItem value="" selected>
          {t("common:class")}
        </MenuItem>
        {filtersData.class.length > 0 &&
          filtersData.class.map((x) => (
            <MenuItem key={x} value={x}>
              {x}
            </MenuItem>
          ))}
      </Select>
      <Select
        displayEmpty
        IconComponent={ExpandMoreRoundedIcon}
        startAdornment={<ThumbDownAltOutlined />}
        inputProps={{ "aria-label": "Without label" }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
        value={filterStatus}
        onChange={handleFilterStatus}
      >
        <MenuItem value="" selected>
          {t("menu:item.rejectionType")}
        </MenuItem>
        <MenuItem key="rejected" value={ObjectStatus.Rejected}>
          {t("menu:item.humanSpotcheck")}
        </MenuItem>
        <MenuItem key="rejectedauto" value={ObjectStatus.RejectedAuto}>
          {t("menu:item.automatedCheck")}
        </MenuItem>
      </Select>
      <FormGroup sx={{ pl: 1 }}>
        <FormControlLabel
          control={<Checkbox checked={withBypassFilter} onClick={() => setWithBypassFilter((prev) => !prev)} />}
          label={<Typography variant="body2">{t("field:label.withBypass")}</Typography>}
        />
      </FormGroup>
    </>
  );

  return (
    <Stack height="100%">
      <DataTable
        loading={isLoading}
        tableKey={TableUuids.INGEST_REJECTED_TABLE}
        columns={ingestProcessingColumns}
        rows={rows.filter(
          (x) =>
            (filterClass === "" || x.assetClass === filterClass) &&
            (filterStatus === "" || x.assetStatus === filterStatus) &&
            (filterType === "" || x.assetType === filterType) &&
            (withBypassFilter === false || x.bypassAllowed) &&
            (filterText === "" ||
              x.assetParentTitleName?.includesAllWords(filterText) ||
              x.assetName?.includesAllWords(filterText) ||
              x.assetId.toString()?.includesAllWords(filterText) ||
              x.message?.some((x) => x.includesAllWords(filterText)))
        )}
        getRowHeight={() => "auto"}
        checkboxSelection
        disableRowSelectionOnClick
        pagination={true}
        selectedRows={selectedItemIds}
        setSelectedRows={setSelectedItems}
        apiRef={apiGrid}
        additionalFilters={additionalToolbarFilters}
        initialState={{
          sorting: {
            sortModel: [{ field: "createdAt", sort: "desc" }],
          },
        }}
        initialColumnVisibility={{
          createdAt: false,
        }}
        onRowClick={showAssetPanel}
      />
      <AssetPanel
        open={openAssetPanel}
        onClose={hideAssetPanel}
        closePanel={hideAssetPanel}
        assetId={selectedAssetId}
        reload={selectedRowId}
      />
      <NewRequestPanel type="create" open={openNewRequestPanel} onClose={hideNewRequestPanel} />
    </Stack>
  );
}

export default IngestRejected;
